import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  AlertCircle, MapPin, ExternalLink, Home, Wallet, Train, Map as MapIcon,
  Shield, Users, Search, Info, ChevronRight, Clock,
} from "lucide-react";

const ICONS = { home: Home, wallet: Wallet, train: Train, map: MapIcon, shield: Shield, users: Users };

/* One category block — six of these anchor the daily-life rail. */
function CategoryCard({ c }) {
  const Icon = ICONS[c.icon] || Home;
  return (
    <div className="moe-card p-6 md:p-7" data-testid={`category-${c.key}`}>
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-full border border-ink flex items-center justify-center bg-tint">
          <Icon size={16} strokeWidth={2.2} />
        </span>
        <h3 className="text-lg md:text-xl font-bold tracking-tight">{c.title}</h3>
      </div>
      {c.note && <p className="mt-3 text-[13px] text-muted leading-snug">{c.note}</p>}
      <div className="mt-5 space-y-4">
        {(c.groups || []).map((g) => (
          <div key={g.label}>
            <div className="text-[10px] uppercase tracking-widest text-soft font-bold">{g.label}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {g.items.map((i) => (
                <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/20 rounded-full bg-paper text-ink">
                  {i}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Affiliate rail — each linkout has disclosure attached. */
function PartnerRow({ title, list, disclosure }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-muted font-bold">{title}</div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2">
        {list.map((r) => (
          <a
            key={r.partner}
            href={r.url}
            target="_blank"
            rel="sponsored noopener"
            data-testid={`partner-${title.replace(/\s+/g, '-').toLowerCase()}-${r.partner}`}
            className="flex items-center justify-between gap-3 border border-ink/15 rounded-lg px-3.5 py-3 hover:bg-tint transition-colors group"
            title={disclosure}
          >
            <div className="min-w-0">
              <div className="font-bold text-ink truncate">{r.partner}</div>
              {r.note && <div className="text-[11px] text-muted truncate">{r.note}</div>}
            </div>
            <ExternalLink size={14} className="text-ink/50 group-hover:text-elec shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

export default function PostArrival() {
  const { country } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [cityQ, setCityQ] = useState("");
  const [cityResult, setCityResult] = useState(null);
  const [activeCat, setActiveCat] = useState(null);

  useEffect(() => {
    api.get(`/post-arrival/${encodeURIComponent(country)}`)
      .then((r) => setData(r.data))
      .catch(() => setData("notfound"));
  }, [country]);

  const resolveCity = async () => {
    const c = cityQ.trim();
    if (!c) return;
    const r = await api.get(`/post-arrival/${encodeURIComponent(country)}/city/${encodeURIComponent(c)}`);
    setCityResult(r.data);
  };

  const filteredCats = useMemo(() => {
    if (!data || data === "notfound") return [];
    return activeCat ? data.categories.filter((c) => c.key === activeCat) : data.categories;
  }, [data, activeCat]);

  if (data === "notfound") {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-center">
        <h1 className="text-3xl font-bold">Post-arrival playbook not ready for {country}.</h1>
        <p className="mt-3 text-muted">Germany is the first country live. Others are landing next.</p>
        <button onClick={() => nav("/countries")} className="mt-6 moe-btn moe-btn-ghost">Back to countries</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-24">
        <div className="moe-shimmer h-40" />
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="moe-shimmer h-56" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 md:pt-16 pb-24">
      <div className="text-xs uppercase tracking-widest text-muted font-bold">Post-arrival dashboard · Germany</div>
      <h1 className="mt-3 text-4xl md:text-6xl font-bold tracking-[-0.03em] leading-[0.98]">
        You've landed. Two weeks starts now.
      </h1>

      {/* --------- Priority action card + visa summary side by side --------- */}
      <div className="mt-10 grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 min-w-0 moe-panel-yolk p-6 md:p-7 rounded-2xl" data-testid="priority-action">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold">
            <AlertCircle size={14} /> Priority action
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-[-0.02em] break-words">{data.priority_action.title}</h2>
          <div className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-bold">
            <Clock size={13} /> {data.priority_action.deadline}
          </div>
          <p className="mt-3 text-[14px] text-ink/85 leading-relaxed">{data.priority_action.why}</p>
        </div>
        <div className="lg:col-span-5 min-w-0 moe-panel-elec p-6 md:p-7 text-white rounded-2xl">
          <div className="text-[11px] uppercase tracking-widest opacity-80 font-bold">{data.visa.type}</div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Fee</div>
              <div className="text-xl font-bold break-words">{data.visa.fee}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Processing</div>
              <div className="text-xl font-bold break-words">{data.visa.processing}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Sperrkonto</div>
              <div className="text-[15px] font-bold leading-tight break-words">{data.visa.sperrkonto.annual}</div>
              <div className="text-[11px] opacity-80 break-words">{data.visa.sperrkonto.monthly}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Statutory ins.</div>
              <div className="text-[13px] font-semibold leading-tight break-words">{data.visa.statutory_insurance}</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20 text-[12px] leading-relaxed break-words">
            <span className="font-bold">{data.visa.recent_update.date}:</span> {data.visa.recent_update.note}
          </div>
        </div>
      </div>

      {/* --------- Category filter chips --------- */}
      <div className="mt-10 flex items-center gap-2 flex-wrap">
        <button onClick={() => setActiveCat(null)}
          data-testid="filter-all"
          className={`px-3.5 py-1.5 text-[13px] font-bold border border-ink rounded-full ${activeCat===null ? "bg-ink text-paper" : "bg-paper text-ink hover:bg-tint"}`}
        >All categories</button>
        {data.categories.map((c) => (
          <button key={c.key} onClick={() => setActiveCat(c.key)}
            data-testid={`filter-${c.key}`}
            className={`px-3.5 py-1.5 text-[13px] font-bold border border-ink rounded-full transition-colors ${activeCat===c.key ? "bg-ink text-paper" : "bg-paper text-ink hover:bg-tint"}`}
          >{c.title}</button>
        ))}
      </div>

      {/* --------- Six category cards --------- */}
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-testid="category-grid">
        {filteredCats.map((c) => <CategoryCard key={c.key} c={c} />)}
      </div>

      {/* --------- City registration office lookup --------- */}
      <div className="mt-14 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-widest text-muted font-bold">City registration offices</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
            Bürgeramt Termin · {data.registration_deadline_days}-day universal deadline
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-2" data-testid="city-office-list">
            {data.city_offices.map((c) => (
              <a key={c.city} href={c.url} target="_blank" rel="noopener"
                data-testid={`city-${c.city}`}
                className="flex items-start gap-3 border border-ink/15 rounded-lg px-4 py-3.5 hover:bg-tint transition-colors group"
              >
                <MapPin size={14} className="mt-0.5 text-elec shrink-0" />
                <div className="min-w-0">
                  <div className="font-bold text-ink">{c.city}</div>
                  {c.note && <div className="text-[11px] text-muted leading-snug">{c.note}</div>}
                  <div className="text-[11px] text-elec truncate">{c.url.replace(/^https?:\/\//, "")}</div>
                </div>
                <ChevronRight size={14} className="ml-auto text-ink/40 group-hover:text-elec shrink-0" />
              </a>
            ))}
          </div>

          <div className="mt-6 border border-ink/15 rounded-xl p-4">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted font-bold">
              <Search size={13} /> Not listed? Safe fallback
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="city-fallback-input"
                type="text" value={cityQ} onChange={(e) => setCityQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && resolveCity()}
                placeholder="Your host city (e.g. Heidelberg)"
                className="flex-1 h-11 px-4 text-[15px] border border-ink rounded-full focus:outline-none focus:ring-2 focus:ring-[color:var(--moe-elec)]/25"
              />
              <button onClick={resolveCity} data-testid="city-fallback-resolve" className="moe-btn moe-btn-primary !py-2 !px-5 text-sm">
                Resolve
              </button>
            </div>
            {cityResult && (
              <div className="mt-4 p-3 rounded-lg bg-tint text-[13px]" data-testid="city-fallback-result">
                <span className="font-bold">{cityResult.city}:</span> {cityResult.resolved === "listed"
                  ? <a href={cityResult.url} target="_blank" rel="noopener" className="text-elec font-semibold underline">{cityResult.url}</a>
                  : <span>{cityResult.hint}</span>}
              </div>
            )}
          </div>
        </div>

        {/* --------- Sperrkonto + APS rules --------- */}
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-muted font-bold">Funds & APS</div>
          <div className="mt-2 moe-card p-6">
            <div className="text-[13px] font-bold">Three valid funds paths</div>
            <ul className="mt-2 space-y-1 text-[14px]">
              {data.visa.sperrkonto.paths.map((p) => (
                <li key={p} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-ink" /> {p}</li>
              ))}
            </ul>
            <div className="mt-5 text-[13px] font-bold">APS Certificate</div>
            <p className="mt-1 text-[13px] text-muted">
              Mandatory unless you meet one of these:
            </p>
            <ul className="mt-2 space-y-1 text-[14px]">
              {data.visa.aps_certificate.exempt_when.map((e) => (
                <li key={e} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--moe-elec)]" /> {e}</li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-ink/10 text-[12px] text-muted">
              Passport: {data.visa.passport} · Travel ins.: {data.visa.travel_insurance}
            </div>
          </div>
        </div>
      </div>

      {/* --------- Referral / partner rail --------- */}
      <div className="mt-14">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted font-bold">Partners</div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">Trusted vendors we've vetted.</h2>
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 text-[12px] text-muted p-3 rounded-lg bg-tint" data-testid="referral-disclosure">
          <Info size={13} className="mt-0.5 shrink-0" />
          {data.referrals.disclosure}
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-8">
          <PartnerRow title="Blocked account"  list={data.referrals.blocked_account} disclosure={data.referrals.disclosure} />
          <PartnerRow title="Accommodation"    list={data.referrals.accommodation}   disclosure={data.referrals.disclosure} />
          <PartnerRow title="Finance & forex"  list={data.referrals.finance}         disclosure={data.referrals.disclosure} />
          <PartnerRow title="Insurance"        list={data.referrals.insurance}       disclosure={data.referrals.disclosure} />
        </div>
      </div>

      {/* --------- Sources --------- */}
      <div className="mt-14 pt-8 border-t border-ink/10">
        <div className="text-xs uppercase tracking-widest text-muted font-bold">Sources · verified {data.verified_at}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.sources.map((s) => (
            <a key={s.label} href={s.url} target="_blank" rel="noopener"
              className="text-[12px] font-semibold text-elec hover:underline underline-offset-4">
              {s.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
